import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Logo from "../assets/sbw 1.png";
import VersionBetaLogo from "../assets/VersionBetaLogo.svg";
import * as Yup from "yup";
import FormField from "../utility/FormField";
import background from "../assets/background.png";
import Countries from "../countries.json";
import { verifyEmail } from "../store/reducers/signup";
import Button from "./Button";
import { toast } from "react-toastify";

const Signup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const [countries, setCountries] = useState([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState("");
  const formDataFromOtp = location.state?.formData;

  const isLoggedIn = useSelector(
    (state) => !!state.reducer.login?.login?.data?.accessToken,
  );
  useEffect(() => {
    if (isLoggedIn) {
      navigate("/dashboard");
    }
  }, [isLoggedIn]);

  useEffect(() => {
    setCountries(Countries.countries);
  }, []);

  const handleCountryChange = (e, setFieldValue) => {
    const selectedCountry = countries.find(
      (country) => country.code === e.target.value,
    );
    setSelectedCountryCode(selectedCountry?.code || "");
    setFieldValue("country", selectedCountry?.name || "");
    setFieldValue("countryCode", selectedCountry?.code || "");
  };

  const validationSchema = Yup.object().shape({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    jobTitle: Yup.string().required("Job title is required"),
    phoneNumber: Yup.string()
      .matches(/^\d+$/, "Phone number must contain only digits")
      .length(10, "Phone number must be exactly 10 digits")
      .required("Number is missing"),
    businessMailId: Yup.string()
      .email("Invalid email format")
      .required("Email ID is required"),
    countryCode: Yup.string().required("Country code is required"),
    organisationType: Yup.string().required("Organization type is required"),
    organisationName: Yup.string().required("Organization name is required"),
    terms: Yup.boolean()
      .oneOf([true], "You must accept the terms and conditions")
      .required("Required"),
  });

  const initialValues = {
    firstName: formDataFromOtp?.firstName || "",
    lastName: formDataFromOtp?.lastName || "",
    businessMailId: formDataFromOtp?.businessMailId || "",
    jobTitle: formDataFromOtp?.jobTitle || "",
    countryCode: formDataFromOtp?.countryCode || "",
    phoneNumber: formDataFromOtp?.phoneNumber || "",
    organisationType: formDataFromOtp?.organisationType || "",
    organisationName: formDataFromOtp?.organisationName || "",
    terms: formDataFromOtp?.terms || false,
  };

  const onSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = { primary_contact_email: values.businessMailId };
      const response = await dispatch(verifyEmail(payload));

      if (response?.payload?.status === "success") {
        if (response.payload.message === "OTP & Mail already verified") {
          navigate("/confirm-signup");
        }

        toast.success(response.payload.message);
        const formDataWithEvorenId = {
          ...values,
          evoren_id: response.payload.data.evoren_id,
        };

        navigate("/verifyotp", { state: { formData: formDataWithEvorenId } });
      }
    } catch (error) {
      toast.error(error.response?.data?.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{
        backgroundImage: `url(${background})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="grid grid-cols-12">
        <div className="col-span-4 md:col-span-6 lg:col-span-8 xl:col-span-8 2xl:col-span-8"></div>
        <div className="min-h-screen p-8 top-[60px] col-span-8 md:col-span-6 lg:col-span-4 xl:col-span-4 2xl:col-span-4 bg-[#0d223d]/60 backdrop-blur-[80px] shadow-lg inline-flex">
          <div className="flex-col justify-center items-center flex">
            <div className="relative inline-block mb-4">
              <img
                src={Logo}
                className="w-full sm:max-w-[140px] lg:max-w-[262px]"
              />

              <img
                src={VersionBetaLogo}
                className="absolute"
                style={{
                  top: "20%",
                  right: "10%",
                  width: "15%"
                }}
              />
            </div>
            <div className="w-full border-t border-[#b3b5bd] mb-6"></div>
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
            >
              {({ isSubmitting, isValid, dirty, setFieldValue }) => (
                <Form className="space-y-2 md:space-y-3 lg:space-y-6 xl:space-y-6 2xl:space-y-8">
                  <span className="space-y-2 md:space-y-3 lg:space-y-6 xl:space-y-6 2xl:space-y-8"></span>
                  <div className="text-[#D2D4DA] text-xs md:text-xs lg:text-sm xl:text-base 2xl:text-xl line-height-[14px] space-y-2 md:space-y-3 lg:space-y-6 xl:space-y-6 2xl:space-y-8">
                    Personal detail - All the sections are mandatory.
                  </div>
                  <div className="flex space-x-3 md:space-x-3 lg:space-x-4 xl:space-x-6 2xl:space-x-10">
                    <FormField label="First name" name="firstName" />
                    <FormField label="Last name" name="lastName" />
                  </div>
                  <div className="flex space-x-3 md:space-x-3 lg:space-x-4 xl:space-x-6 2xl:space-x-10">
                    <FormField label="Business mail ID" name="businessMailId" />
                    <FormField label="Job title" name="jobTitle" />
                  </div>
                  <div className="flex space-x-3 md:space-x-3 lg:space-x-4 xl:space-x-6 2xl:space-x-10">
                    <FormField
                      label="Country name"
                      name="countryCode"
                      as="select"
                      onChange={(e) => handleCountryChange(e, setFieldValue)}
                      options={countries.map((country, index) => {
                        const codeLength = country.code.length;
                        const truncateLength = Math.max(8, 15 - codeLength);  // minimum length 8

                        const shortName =
                          country.name.length > truncateLength
                            ? country.name.slice(0, truncateLength) + "..."
                            : country.name;

                        return {
                          value: country.code,
                          label: `${country.code}-${shortName}`,   // truncated UI label
                          fullLabel: `${country.code}-${country.name}`, // full name if needed
                          key: `${country.name}-${country.code}-${index}`,
                        };
                      })}
                    />
                    <FormField
                      label="Phone number"
                      name="phoneNumber"
                      placeholder={selectedCountryCode}
                    />
                  </div>

                  <FormField
                    label="Organization Type"
                    name="organisationType"
                    as="select"
                    options={[
                      { value: "Maritime & Ports", label: "Maritime & Ports" },
                      { value: "Aviation", label: "Aviation" },
                      { value: "Intelligence", label: "Intelligence" },
                      { value: "Mobility", label: "Mobility" },
                      { value: "Others", label: "Others" },
                    ]}
                  />
                  <div className="space-y-6 2xl:space-y-12">
                    <FormField
                      label="Organization name"
                      name="organisationName"
                    />

                    <FormField
                      label="By checking this, you agree to Companies’s T&C and General Data Protection Act (GDPA) policies.*"
                      name="terms"
                      as="checkbox"
                    />
                    <div className="mt-8">
                      <Button
                        type="submit"
                        disabled={isSubmitting || !isValid || !dirty}
                        primary={isValid && dirty ? true : false}
                      >
                        Verify details
                      </Button>
                    </div>
                    <button
                      type="button"
                      className="mt-3 w-full text-gray-300 text-xs md:text-xs lg:text-sm xl:text-base 2xl:text-lg font-normal p-1 rounded-lg transition duration-300"
                      onClick={() => navigate("/login")}
                    >
                      Login
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
